import React from 'react';
import { Package, Settings, Check } from 'lucide-react';
import { PlatformCard } from '../../../components/common';
import InfoTooltip from '../../../components/common/InfoTooltip';

const PlatformStep = ({ quoteData, setQuoteData, pricing }) => {
  const deployments = [
    {
      id: 'On-Premise',
      name: 'On-Premise',
      ...pricing.deployment['On-Premise']
    },
    {
      id: 'Private Hosting',
      name: 'Private Hosting',
      ...pricing.deployment['Private Hosting']
    },
    {
      id: 'Public Cloud',
      name: 'Public Cloud (SaaS)',
      ...pricing.deployment['Public Cloud']
    }
  ];

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Platform</h2>
      <p className="text-slate-600 mb-8">Choose your platform and deployment model</p>
      
      <div className="space-y-8">
        {/* Platform Selection */}
        <div>
          <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
            <Package size={18} className="text-blue-600" />
            Platform Foundation
            <InfoTooltip
              text="SplashBI is the core analytics platform required for all deals. SplashAI adds AI-powered predictive analytics and natural language queries as an optional add-on."
              position="right"
            />
          </h3>
          
          <div className="space-y-4">
            <PlatformCard
              title="SplashBI Platform"
              description="Core platform for HCM, Finance, CRM, SCM solutions"
              price="$50K"
              sku={pricing.platform.SplashBI.sku}
              selected={quoteData.hasSplashBI}
              onToggle={() => setQuoteData({...quoteData, hasSplashBI: !quoteData.hasSplashBI})}
              features={['Multi-Source Integration', 'AI-Powered Insights', 'Advanced Analytics']}
              color="blue"
            />

            {quoteData.hasSplashBI && (
              <PlatformCard
                title="SplashAI Platform"
                description="Advanced AI-powered analytics and predictions"
                price="+$50K"
                sku={pricing.platform.SplashAI.sku}
                selected={quoteData.hasSplashAI}
                onToggle={() => setQuoteData({...quoteData, hasSplashAI: !quoteData.hasSplashAI})}
                features={['Predictive Analytics', 'Natural Language Queries', 'Automated Insights']}
                color="purple"
                addon
              />
            )}
          </div>
        </div>

        {/* Deployment */}
        {quoteData.hasSplashBI && (
          <div>
            <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Settings size={18} className="text-blue-600" />
              Deployment Model
              <InfoTooltip
                text="On-Premise: Customer hosts on their servers. Private Hosting: SplashBI hosts dedicated infrastructure. Public Cloud (SaaS): Shared multi-tenant cloud with lowest cost."
                position="right"
              />
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {deployments.map(deploy => (
                <button
                  key={deploy.id}
                  onClick={() => setQuoteData({...quoteData, deployment: deploy.id})}
                  className={`p-5 rounded-xl border-2 transition-all text-left ${
                    quoteData.deployment === deploy.id 
                      ? 'border-blue-600 bg-blue-50' 
                      : 'border-slate-200 hover:border-blue-300'
                  }`}
                >
                  <h4 className="font-semibold text-slate-800 mb-1">{deploy.name}</h4>
                  <p className="text-xs text-slate-400 mb-2">SKU: {deploy.sku}</p>
                  <p className="text-sm text-slate-600 mb-3">{deploy.desc}</p>
                  <div className="text-lg font-bold text-blue-600">
                    {deploy.price > 0 ? `$${(deploy.price / 1000).toFixed(0)}K/year` : 'Included'}
                  </div>
                  {quoteData.deployment === deploy.id && (
                    <div className="mt-3 flex items-center justify-center gap-2 text-blue-600">
                      <Check size={16} />
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformStep;
